import React, { useCallback, useEffect, useState } from "react";
import {
  EuiBadge,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButton,
  EuiEmptyPrompt,
  EuiSpacer,
  EuiText,
  EuiTitle,
  EuiCallOut,
} from "@elastic/eui";
import {
  ExternalAccessRule,
  ExternalAccessRules,
  ExternalRuleEnforcement,
  useApi,
} from "../";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";
import { TableSkeleton } from "src/components/skeleton";

const ENFORCEMENT_COLOR: Record<ExternalRuleEnforcement, string> = {
  server: "success",
  role: "primary",
  "ui-only": "warning",
};

/**
 * Read-only, verifiable overview of the stricter rules that apply to external
 * (non-autonubil) users on questionnaires. Data comes from the authoritative
 * registry at /api/v2/admin/external-rules (functions/auth/externalRules.ts).
 */
export const AdminExternalAccess = (): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const ENFORCEMENT_LABEL: Record<ExternalRuleEnforcement, string> = {
    server: translate("anketa.admin.external.enforcement.server", {
      defaultMessage: "Server (hard)",
    }),
    role: translate("anketa.admin.external.enforcement.role", {
      defaultMessage: "Role (ABAC)",
    }),
    "ui-only": translate("anketa.admin.external.enforcement.ui_only", {
      defaultMessage: "UI only (gap)",
    }),
  };

  const [data, setData] = useState<ExternalAccessRules | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!keycloak?.token) {
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const resp = await Get<any>({
        path: "v2/admin/external-rules",
        responseType: "json",
        token: keycloak.token,
      });
      if (!resp.success && (resp.status === 403 || resp.status === 401)) {
        setForbidden(true);
        return;
      }
      const payload: ExternalAccessRules | null =
        (resp?.data as any)?.data ?? null;
      if (payload) {
        setData(payload);
      } else {
        setError(
          resp.error?.message ||
            translate("anketa.admin.external.load.error", {
              defaultMessage: "Rules could not be loaded.",
            }),
        );
      }
    } catch {
      setError(
        translate("anketa.admin.external.load.error", {
          defaultMessage: "Rules could not be loaded.",
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <TableSkeleton rows={6} />;
  }
  if (forbidden) {
    return (
      <EuiEmptyPrompt
        iconType="lock"
        color="warning"
        title={
          <h2>
            {translate("anketa.admin.forbidden.title", {
              defaultMessage: "Access denied",
            })}
          </h2>
        }
        body={
          <p>
            {translate("anketa.admin.forbidden.body", {
              defaultMessage:
                "You do not have permission to view this section.",
            })}
          </p>
        }
      />
    );
  }
  if (error || !data) {
    return (
      <EuiEmptyPrompt
        iconType="alert"
        color="danger"
        title={
          <h2>
            {error ??
              translate("anketa.admin.external.no_data", {
                defaultMessage: "No data",
              })}
          </h2>
        }
        actions={
          <EuiButton
            color="primary"
            fill
            iconType="refresh"
            onClick={() => load()}
          >
            {translate("anketa.common.retry", { defaultMessage: "Retry" })}
          </EuiButton>
        }
      />
    );
  }

  const columns: EuiBasicTableColumn<ExternalAccessRule>[] = [
    {
      name: translate("anketa.admin.external.column.area", {
        defaultMessage: "Area",
      }),
      render: (r: ExternalAccessRule) => (
        <EuiText size="s">
          <strong>{r.area}</strong>
        </EuiText>
      ),
    },
    {
      name: translate("anketa.admin.external.column.rule", {
        defaultMessage: "Rule",
      }),
      render: (r: ExternalAccessRule) => <EuiText size="s">{r.rule}</EuiText>,
    },
    {
      name: translate("anketa.admin.external.column.enforcement", {
        defaultMessage: "Enforcement",
      }),
      width: "140px",
      render: (r: ExternalAccessRule) => (
        <EuiBadge color={ENFORCEMENT_COLOR[r.enforcement]}>
          {ENFORCEMENT_LABEL[r.enforcement]}
        </EuiBadge>
      ),
    },
    {
      name: translate("anketa.admin.external.column.internal_only", {
        defaultMessage: "Internal only",
      }),
      width: "110px",
      align: "center",
      render: (r: ExternalAccessRule) =>
        r.internalOnly ? (
          <EuiBadge color="hollow" iconType="lock">
            {translate("anketa.admin.external.badge.internal", {
              defaultMessage: "internal",
            })}
          </EuiBadge>
        ) : (
          <EuiText size="s" color="subdued">
            -
          </EuiText>
        ),
    },
  ];

  return (
    <>
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.external.description", {
          defaultMessage:
            "Overview of the stricter rules for external users (non-autonubil) on questionnaires. Read-only - for traceability of the policy.",
        })}
      </EuiText>
      <EuiSpacer size="m" />

      <EuiTitle size="xs">
        <h3>
          {translate("anketa.admin.external.access_model.title", {
            defaultMessage: "Access model",
          })}
        </h3>
      </EuiTitle>
      <EuiSpacer size="xs" />
      <EuiText size="s">
        <ul>
          {data.accessModel.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </EuiText>
      <EuiSpacer size="m" />

      <EuiTitle size="xs">
        <h3>
          {translate("anketa.admin.external.rules.title", {
            defaultMessage: "Rules",
          })}
        </h3>
      </EuiTitle>
      <EuiSpacer size="xs" />
      <EuiBasicTable<ExternalAccessRule>
        items={data.rules}
        columns={columns}
        tableLayout="auto"
      />
      <EuiSpacer size="m" />

      {data.knownGaps.length > 0 && (
        <EuiCallOut
          title={translate("anketa.admin.external.known_gaps.title", {
            defaultMessage: "Known limitations",
          })}
          color="warning"
          iconType="alert"
        >
          <ul>
            {data.knownGaps.map((gap, i) => (
              <li key={i}>{gap}</li>
            ))}
          </ul>
        </EuiCallOut>
      )}
    </>
  );
};
